import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {ErrorModel} from '../app.model';
import {ACTION_TYPES} from '../app.types';
import {
  AccountWithMappings,
  ApiId,
  DistributionAccountModel,
  TtdPriceModel
} from '../distribution-account/distribution-account.model';
import {License} from '../segment/info/shared-targets-list/account-google-ddp/domain';
import {Segment} from '../segment/segment.model';
import {AppStore} from '../store/store';
import {TtdDataRate} from '../ttd/ttd-data-rate/ttd-data-rate.types';
import {HttpClientWithCache} from '../utils/http-client-with-cache';
import {HttpClientUtils} from '../utils/http-client.utils';
import {HttpInteraction} from '../utils/http-interactions.utils';
import {TargetAccount, TargetModel} from './target.model';
import {loadSegmentDetails, reloadSegmentList} from '../segment/segments.state';
import {targetUnshared} from './target.state';
import {
  Share,
  ShareAccount,
  ShareMessageType,
  ShareRequest,
  ShareResult,
  TheTradeDeskPriceShare
} from '../segment-multi-share/domain';

@Injectable({providedIn: 'root'})
export class TargetService {

  static statusMap: { [key: string]: string } = {
    Successful: 'check_circle',
    NotStarted: 'pending',
    InProgress: 'sync',
    Failed: 'error_outline',
    DEFAULT: 'help_outline'
  };

  constructor(
    public appStore: AppStore,
    private httpClientUtils: HttpClientUtils,
    private httpClient: HttpClientWithCache) {
  }

  static getStatusIcon(status: string) {
    let icon = 'help_outline';
    const statusMap = TargetService.statusMap;
    if (statusMap[status]) {
      icon = statusMap[status];
    }
    return icon;
  }

  getAllTargetsIgnoringPartners(): Observable<AccountWithMappings[]> {
    const url = `${environment.apiSegments}/accounts-all`;
    return this.httpClient.getWithCache<AccountWithMappings[]>(url);
  }

  getAllTargets(): Observable<TargetAccount[]> {
    const url = `${environment.apiSegments}/accounts`;
    return this.httpClient.get<TargetAccount[]>(url)
      .pipe(
        map(value => value
          .map(target => ({
            ...target,
            accounts: target.accounts
              .map(acc => ({
                ...acc,
                segmentId: acc.firstShare?.segmentId,
                ttdCategoryId: acc.firstShare?.ttdCategoryId,
                ttdPricing: acc.firstShare?.ttdPricing,
                ttdCompliance: acc.firstShare?.ttdCompliance,
                ttdDisplayName: acc.firstShare?.ttdDisplayName
              }))
          }))));
  }

  targetsSharedForSegment(segment: Segment, errorModels: ErrorModel[]) {
    this.appStore.dispatch(loadSegmentDetails({segmentId: segment.id}));
    this.appStore.dispatch(reloadSegmentList());
    this.appStore.dispatch({type: ACTION_TYPES.TARGETS_SHARED_FOR_SEGMENT, errors: errorModels});
  }

  shareMultipleSegmentsToTargets(segmentIds: number[],
                                 distributionAccounts: ShareAccount[]) {
    const url = `${environment.apiSegments}/multisegmentshare`;
    const body = {
      segmentIds,
      accounts: distributionAccounts,
      type: 'SHARE'
    };

    return this.httpClient.post(url, body, {responseType: 'text'});
  }

  bulkUnshare(segmentIds: number[],
              distributionAccounts: ShareAccount[]) {
    const url = `${environment.apiSegments}/multisegmentshare`;
    const body = {
      segmentIds,
      accounts: distributionAccounts,
      type: 'UNSHARE'
    };

    return this.httpClient.post(url, body, {responseType: 'text'});
  }

  getMultishareResultsById(id: string) {
    return this.httpClient.get<Share>(`${environment.apiSegments}/multishare/history/${id}`);
  }

  shareSegmentWithTargets(targets: TargetAccount[], segment: Segment) {
    const accountsToShare: ShareAccount[] = [];
    // TODO: request argument might not be passed correctly to httpClient
    const request: ShareRequest = {segmentId: segment.id, accounts: accountsToShare};
    const errorModels: ErrorModel[] = [];

    targets.forEach((target: TargetAccount) => {
      target.accounts.forEach((account) => {
        if (account.selected) {
          let segmentSharing = {distributionId: account.id};

          if (account.userProvidedForeignId) {
            segmentSharing = Object.assign(segmentSharing, {foreignId: account.foreignIdForCurrentSegment});
          }

          if (account.apiId === ApiId.TTD) {
            segmentSharing = Object.assign(segmentSharing, {
              additionalSharingConfiguration: {
                theTradeDesk: {
                  categoryId: (account as DistributionAccountModel).ttdCategoryId
                }
              }
            });
          }
          accountsToShare.push(segmentSharing);
        }
      });
    });

    if (accountsToShare.length > 0) {
      this.httpClientUtils.post(`${environment.apiSegments}/shares`, request)
        .onSuccess((response) => {
          const results = response as unknown as ShareResult[] || [];
          (results || []).forEach(result => {
            result.shareMessages.forEach(message => {
              if (message.type !== ShareMessageType.SUCCESS) {
                errorModels.push(HttpInteraction.errorModelFromErrorMessage(message));
              }
            });
          });
          this.targetsSharedForSegment(segment, errorModels);
        })
        .onError((error) => {
          errorModels.push(HttpInteraction.errorModel(error));
          this.targetsSharedForSegment(segment, errorModels);
        })
        .doIt();
    }
  }

  unshareSegment(accountId: number, segmentId: number) {
    this.httpClientUtils.delete(`${environment.apiSegments}/accounts/${accountId}/segments/${segmentId}`)
      .onSuccess(() => {
        this.appStore.dispatch(targetUnshared({segment: {id: segmentId} as Segment}));
      })
      .onError((error) => {
        this.appStore.dispatch({type: ACTION_TYPES.TARGET_ERROR, error: HttpInteraction.errorModel(error)});
      })
      .doIt();
  }

  getTtdCategories(): Observable<TtdCategory[]> {
    return this.httpClient.getWithCache<TtdCategory[]>(`${environment.apiSegments}/ttd/category`);
  }

  getTtdCategoriesNoCache(): Observable<TtdCategory[]> {
    return this.httpClient.get<TtdCategory[]>(`${environment.apiSegments}/ttd/category`);
  }

  getTtdPartners(): Observable<TtdPartner[]> {
    return this.httpClient.get<TtdPartner[]>(`${environment.apiSegments}/ttd/partner`);
  }

  createTtdBuyer(request: TtdPartner): Observable<TtdPartner> { // todo rename
    return this.httpClient.post<TtdPartner>(`${environment.apiSegments}/ttd/partner`, request);
  }

  updateTtdPartner(request: TtdPartner): Observable<TtdPartner> {
    return this.httpClient.put<TtdPartner>(`${environment.apiSegments}/ttd/partner`, request);
  }

  getTtdGroups(): Observable<TtdGroup[]> {
    return this.httpClient.get<TtdGroup[]>(`${environment.apiSegments}/ttd/partner-group`);
  }

  getTtdConfig(): Observable<TtdConfig[]> {
    return this.httpClient.get<TtdConfig[]>(`${environment.apiSegments}/ttd/ttd-config`);
  }

  addTtdPrice(accountId: number, segmentId: number, ttdPrice: TtdPriceModel): Observable<TtdPriceModel> {
    const ttdPriceShare: TheTradeDeskPriceShare = {
      accountId,
      segmentId,
      rate: {
        cpmAmountUsd: ttdPrice.cpm || 0,
        percentOfMediaCostRate: ttdPrice.percentOfMediaCostRate,
        rateType: ttdPrice.type
      } as TtdDataRate,
      buyerId: ttdPrice.buyerId as string,
      groupId: ttdPrice.groupId,
      brandId: ttdPrice.brandId
    };
    return this.httpClient.put<TtdPriceModel[]>(`${environment.apiSegments}/ttd/rate`, ttdPriceShare)
      .pipe(
        switchMap((results) => of(results[0]))
      );
  }

  updateTtdBuyableStatus(request: TtdBuyable): Observable<any> {
    return this.httpClient.put(`${environment.apiSegments}/ttd/buyable`, request);
  }

  getTtdBuyableStatus(foreignId: string, accountId: number | undefined): Observable<TtdBuyable> {
    return this.httpClient.get<TtdBuyable>(`${environment.apiSegments}/ttd/buyable/${foreignId}?accountId=${accountId}`);
  }

  createTtdCategory(ttdCategory: TtdCategoryRequest): Observable<TtdCategory> {
    return this.httpClient.post<TtdCategory>(`${environment.apiSegments}/ttd/category`, ttdCategory);
  }

  addTtdCategoryGroupRate(categoryId: number, groupId: number, rate: TtdDataRate) {
    return this.httpClient.post(
      `${environment.apiSegments}/ttd/category/${categoryId}/group-rate`, {groupId, rate}
    );
  }

  createTtdPartnerGroup(request: TtdGroup): Observable<TtdGroup> {
    return this.httpClient.post<TtdGroup>(`${environment.apiSegments}/ttd/partner-group`, request);
  }

  addGoogleDdpPrice(licensee: License) {
    return this.httpClient.post<License>(`${environment.apiSegments}/google/ddp/licensees`, licensee);
  }

  updateGoogleDdpPrice(licensee: License) {
    return this.httpClient.put<License>(`${environment.apiSegments}/google/ddp/licensees/${licensee.userListId}/${licensee.clientId}`,
      {
        clientProduct: licensee.clientProduct,
        cost: licensee.cost,
        clientCustomerName: licensee.clientCustomerName
      });
  }

  downloadXlsxPrices() {
    return this.httpClient.get(`${environment.apiSegments}/export-segment-prices`, {responseType: 'blob'});
  }

  updateName(segmentId: number | undefined, name: string, accountId: number) {
    return this.httpClient.put(`${environment.apiSegments}/ttd/name`, {segmentId, name, accountId});
  }
}

export interface TargetAction {
  type: string;
  targets?: TargetModel[];
  targetsForSegment?: { targets: TargetModel[] };
  error?: any;
  errors?: ErrorModel[];
  unsharedAccountId?: number;
  filterAccounts?: DistributionAccountModel[];
}

export interface TtdCategory {
  id: number;
  providerElementId: string;
  displayName: string;
  origin: string;
}

/**
 * Contains information about the current system price.A system price can be owned by a {@link TtdCategory}.
 *
 * The combination aof `amount` and `percentOfMediaCost` depends on the selected `rateType`:
 *    - CPM: only `amount`
 *    - PercentOfMediaCost: only `percentOfMediaCost`
 *    - Hybrid: both
 */
export interface SystemPriceDto {
  // the cpm or cap amount
  cpmAmountUsd?: number;
  percentOfMediaCostRate?: number;
  rateType: TtdRateType;
  rateTypeRequested?: TtdRateType;
  status: string | TtdPriceStatus;
  estimatedProcessAt?: string;
  cpmAmountUsdRequested?: number;
  percentOfMediaCostRateRequested?: number;
  /**
   * Optional field that holds the last updated by a user to TTD date.
   */
  lastUpdated?: string;
}

export declare type TtdPriceStatus = 'Successful' | 'NotStarted' | 'InProgress' | 'Failed';

export interface TtdSystemPriceRequest {
  cpmAmountUsd?: number;
  percentOfMediaCostRate?: number;
  rateType: TtdRateType;
  providerElementIds: string[];
}

export interface TtdSBatchRateRequest {
  cpmAmountUsd?: number;
  percentOfMediaCostRate?: number;
  rateType: TtdRateType;
  providerElementIds: string[];
  groupOrSystemId: number;
  accountId: number;
}

// TODO remove CPM.
export enum TtdRateType {
  CPM = 'CPM',
  PercentOfMediaCost = 'PercentOfMediaCost',
  Hybrid = 'Hybrid'
}

export interface TtdCategoryRequest {
  parentId: string;
  displayName: string;
  origin: string;
}

export interface TTDPartnerGroupRate {
  partner: TtdPartner;
  cpmAmountUsd?: number;
  percentOfMediaCostRate?: number;
  rateType: TtdRateType;
  rateTypeRequested?: TtdRateType;
  status: string | TtdPriceStatus;
  estimatedProcessAt?: string;
  cpmAmountUsdRequested?: number;
  percentOfMediaCostRateRequested?: number;
  rateProcessedAt?: string | Date;
}

export interface TtdPartner {
  partnerName: string;
  partnerId: string;
  group?: TtdGroup;
}

export interface TtdGroup {
  id?: number;
  name: string;
}

export interface TtdBuyable {
  foreignId: string;
  buyable: boolean;
  accountId?: number;
}

export interface TtdConfig {
  id: number;
  brandId: string;
  providerId: string;
  apiId: string;
}
